import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/api/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Bell, CheckCircle2, ClipboardList, Package, Users,
  FileText, Mail, Info, CheckCheck, X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';

const NOTIFICATION_CONFIG = {
  task_completed: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  task_assigned: { icon: ClipboardList, color: 'text-blue-600', bg: 'bg-blue-50' },
  pdf_created: { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50' },
  offer_sent: { icon: Mail, color: 'text-orange-600', bg: 'bg-orange-50' },
  order_created: { icon: Package, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  lead_assigned: { icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  lead_status_changed: { icon: Users, color: 'text-amber-600', bg: 'bg-amber-50' },
  system: { icon: Info, color: 'text-slate-600', bg: 'bg-slate-50' },
};

function getNavigationTarget(notification) {
  const { reference_type } = notification;
  switch (reference_type) {
    case 'task': return 'AdminTasks';
    case 'order': return 'AdminOrders';
    case 'lead': return 'AdminLeads';
    case 'offer': return 'AdminInbox';
    default: return null;
  }
}

export default function NotificationCenter() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications', user?.email],
    queryFn: () => base44.entities.Notification.filter(
      { recipient_email: user?.email },
      '-created_date'
    ),
    enabled: !!user?.email,
    refetchInterval: 30000, // 30 seconds polling
  });

  const markReadMutation = useMutation({
    mutationFn: (id) => base44.entities.Notification.update(id, { is_read: true }),
    onSuccess: () => queryClient.invalidateQueries(['notifications']),
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const unread = notifications.filter(n => !n.is_read);
      await Promise.all(unread.map(n =>
        base44.entities.Notification.update(n.id, { is_read: true })
      ));
    },
    onSuccess: () => queryClient.invalidateQueries(['notifications']),
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }
    const target = getNavigationTarget(notification);
    if (target) {
      navigate(createPageUrl(target));
      setOpen(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMin < 1) return 'Gerade eben';
      if (diffMin < 60) return `vor ${diffMin} Min.`;
      if (diffHours < 24) return `vor ${diffHours} Std.`;
      if (diffDays < 7) return `vor ${diffDays} Tagen`;
      return format(date, 'dd.MM.yyyy', { locale: de });
    } catch {
      return '';
    }
  };

  return (
    <>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(true)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
      >
        <Bell className="w-5 h-5 text-white/70" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-in zoom-in duration-200">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0">
          <SheetHeader className="px-4 py-3 border-b bg-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Benachrichtigungen
                {unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white h-5 px-1.5">{unreadCount}</Badge>
                )}
              </SheetTitle>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => markAllReadMutation.mutate()}
                  className="text-xs"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Alle gelesen
                </Button>
              )}
            </div>
          </SheetHeader>

          <div className="overflow-y-auto max-h-[calc(100vh-80px)]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Bell className="w-12 h-12 mb-3 opacity-30" />
                <p className="font-medium">Keine Benachrichtigungen</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.slice(0, 50).map(notification => {
                  const config = NOTIFICATION_CONFIG[notification.type] || NOTIFICATION_CONFIG.system;
                  const Icon = config.icon;

                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        'w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3',
                        !notification.is_read && 'bg-blue-50/50'
                      )}
                    >
                      <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5', config.bg)}>
                        <Icon className={cn('w-4 h-4', config.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn(
                            'text-sm leading-snug',
                            !notification.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'
                          )}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        {notification.message && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notification.message}</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">{formatTime(notification.created_date)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
